import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { X, Pencil, Trash2, RotateCcw, RotateCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ImageUploadButton } from "../ImageUploadButton";
import { SortableImageGrid } from "../SortableImageGrid";
import type { VehicleTestImages, DamageImage } from "@/types/vehicleTest";
import { photoSessionSourceProps } from "../../photoSession/photoSessionButtonProps";

interface DamageSectionProps {
  damageImages: DamageImage[];
  uploadingImages: Set<string>;
  expandedComments: { [key: number]: boolean };
  commentTextsRef: React.MutableRefObject<{ [key: number]: string }>;
  onImageClick: (image: any, alt: string) => void; // Changed to pass full image object
  getThumbnailUrl?: (image: any) => string;
  onRemoveImage: (section: keyof VehicleTestImages, index: number) => void;
  onRotate?: (image: any, direction: "left" | "right") => void;
  onToggleComment: (index: number) => void;
  onSaveComment: (index: number) => void;
  onCancelComment: (index: number) => void;
  onReorder?: (newOrder: DamageImage[]) => void;
  onFileSelect: (files: File[]) => Promise<void>;
  onSelect: () => void;
  onCameraSelect: () => void;
  rotatingImageId?: string | null;
  getImageRotation?: (imageId: string) => number;
}

export const DamageSection: React.FC<DamageSectionProps> = ({
  damageImages: damageImagesProp,
  uploadingImages,
  expandedComments,
  commentTextsRef,
  onImageClick,
  getThumbnailUrl,
  onRemoveImage,
  onRotate,
  onToggleComment,
  onSaveComment,
  onCancelComment,
  onReorder,
  onFileSelect,
  onSelect,
  onCameraSelect,
  rotatingImageId,
  getImageRotation,
}) => {
  const { language } = useLanguage();
  const lp = listingPostLabels(language);
  const sourceProps = photoSessionSourceProps(lp.images);
  const damageImages = damageImagesProp || [];
  const canAddMore = damageImages.length < 15;

  const gridContent = onReorder ? (
    <SortableImageGrid
      images={damageImages}
      section="damage"
      onImageClick={onImageClick}
      getThumbnailUrl={getThumbnailUrl}
      onRemoveImage={(index) => onRemoveImage("damage", index)}
      onRotate={onRotate}
      onReorder={onReorder}
      rotatingImageId={rotatingImageId}
      getImageRotation={getImageRotation}
      rowFooter={(index) => {
        const saved = damageImages[index]?.comment?.trim();
        const open = expandedComments[index];
        return (
          <div className="mt-1.5 space-y-2 w-full min-w-0">
            {open && (
              <div className="space-y-2">
                <Textarea
                  defaultValue={damageImages[index]?.comment || ""}
                  onChange={(e) => {
                    commentTextsRef.current[index] = e.target.value;
                  }}
                  placeholder={lp.vehicleTest.addCommentPlaceholder}
                  className="w-full p-2 border rounded bg-card text-sm"
                  rows={3}
                />
                <div className="grid grid-cols-2 gap-2 w-full min-w-0">
                  <Button
                    size="sm"
                    className="w-full min-w-0"
                    onClick={() => onSaveComment(index)}
                  >
                    {lp.vehicleTest.save}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full min-w-0"
                    onClick={() => onCancelComment(index)}
                  >
                    {lp.vehicleTest.cancel}
                  </Button>
                </div>
              </div>
            )}
            {!open && saved && (
              <div className="rounded border bg-muted/50 p-2 text-xs break-words min-w-0 space-y-2">
                <p className="w-full min-w-0 whitespace-pre-wrap">{saved}</p>
                <div className="flex justify-end gap-0.5 pt-0.5 border-t border-border/60">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    aria-label={lp.vehicleTest.editComment ?? "Redigera"}
                    onClick={() => onToggleComment(index)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    aria-label={lp.vehicleTest.deleteComment ?? "Ta bort"}
                    onClick={() => {
                      commentTextsRef.current[index] = "";
                      void onSaveComment(index);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            {!open && !saved && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full h-9 text-xs font-medium"
                onClick={() => onToggleComment(index)}
              >
                {`+ ${lp.vehicleTest.addComment ?? "Kommentar"}`}
              </Button>
            )}
          </div>
        );
      }}
    >
      {canAddMore && (
        <ImageUploadButton
          onSelect={onSelect}
          onFileSelect={onFileSelect}
          onCameraSelect={onCameraSelect}
          label={
            <>
              {lp.serviceDetails.uploadImages}{" "}
              <span className="font-semibold">
                {lp.serviceDetails.maxPerBatch}
              </span>
            </>
          }
          isThumbnail={false}
          disabled={false}
          isLoading={uploadingImages.size > 0}
          section="damage"
          {...sourceProps}
        />
      )}
    </SortableImageGrid>
  ) : null;

  return (
    <Card className="border-0">
      <CardContent className="pt-4">
        {onReorder ? (
          gridContent
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-min min-w-0">
            {damageImages.map((damage, index) => (
              <div key={index} className="relative min-w-0 w-full max-w-full">
                <div className="relative group">
                  <img
                    src={getThumbnailUrl ? getThumbnailUrl(damage) : damage.url}
                    alt={`${lp.images.damageImageAlt || lp.images.damage} ${index + 1}`}
                    className="w-full h-24 object-cover rounded border cursor-pointer"
                    onClick={() =>
                      onImageClick(
                        damage,
                        `${lp.images.damageImageAlt || lp.images.damage} ${index + 1}`,
                      )
                    }
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveImage("damage", index);
                    }}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  {onRotate && (
                    <div className="absolute bottom-1 left-1 flex gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRotate(damage, "left");
                        }}
                        className="p-1 rounded bg-black/60 hover:bg-black/80 text-white"
                        aria-label="Rotera vänster"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRotate(damage, "right");
                        }}
                        className="p-1 rounded bg-black/60 hover:bg-black/80 text-white"
                        aria-label="Rotera höger"
                      >
                        <RotateCw className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {expandedComments[index] && (
                  <div className="mt-2 space-y-2">
                    <Textarea
                      defaultValue={damage.comment || ""}
                      onChange={(e) => {
                        commentTextsRef.current[index] = e.target.value;
                      }}
                      placeholder={lp.vehicleTest.addCommentPlaceholder}
                      className="w-full p-2 border rounded bg-card"
                      rows={3}
                    />
                    <div className="grid grid-cols-2 gap-2 w-full min-w-0">
                      <Button
                        size="sm"
                        className="w-full min-w-0"
                        onClick={() => onSaveComment(index)}
                      >
                        {lp.vehicleTest.save}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full min-w-0"
                        onClick={() => onCancelComment(index)}
                      >
                        {lp.vehicleTest.cancel}
                      </Button>
                    </div>
                  </div>
                )}

                {!expandedComments[index] && damage.comment?.trim() && (
                  <div className="mt-2 rounded border bg-muted/50 p-2 text-sm break-words overflow-wrap-anywhere min-w-0 space-y-2">
                    <p className="w-full min-w-0 whitespace-pre-wrap">
                      {damage.comment}
                    </p>
                    <div className="flex justify-end gap-0.5 pt-0.5 border-t border-border/60">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        aria-label={lp.vehicleTest.editComment ?? "Redigera"}
                        onClick={() => onToggleComment(index)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        aria-label={lp.vehicleTest.deleteComment ?? "Ta bort"}
                        onClick={() => {
                          commentTextsRef.current[index] = "";
                          void onSaveComment(index);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {!expandedComments[index] && !damage.comment?.trim() && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full h-9 text-xs font-medium"
                    onClick={() => onToggleComment(index)}
                  >
                    {`+ ${lp.vehicleTest.addComment ?? "Kommentar"}`}
                  </Button>
                )}
              </div>
            ))}

            {canAddMore && !onReorder && (
              <ImageUploadButton
                onSelect={onSelect}
                onFileSelect={onFileSelect}
                onCameraSelect={onCameraSelect}
                label={
                  <>
                    {lp.serviceDetails.uploadImages}{" "}
                    <span className="font-semibold">
                      {lp.serviceDetails.maxPerBatch}
                    </span>
                  </>
                }
                isThumbnail={false}
                disabled={false}
                isLoading={uploadingImages.size > 0}
                section="damage"
                {...sourceProps}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
